<template>
  <div class="content-selection">
    <h1 class="f4 title">{{ $t('Content to launch remotely:') }}</h1>
    <div class="content">
      <div class="selectionradio">
        <input type="radio" id="content" :checked="launchChoice === 'content'" @change="setLaunchChoiceContent">
        <div class="selectedcontent _input-style" @click="openContentList">
          <div class="placeholder f6" v-if="!contentSelection">{{ $t("Select a title") }}</div>

          <div class="contentitem" v-if="contentSelection">
            <div class="image"><RatioContainer><img :src="contentSelection.imageUrl" :alt="contentSelection.title"></RatioContainer></div>
            <span>{{contentSelection.title}}</span>
          </div>

          <UIIcon name="chevron-down" />
        </div>
      </div>

      <div class="contentlist" :class="{ '_open': open, 'dark-mode' : darkMode }">
        <UIInput type="search" v-model="search" />
        <ul>
          <li
            class="contentitem f5"
            v-for="singleContent in contentSearch"
            @click="selectContent(singleContent)"
            :key="singleContent.id"
          >
            <div class="image"><RatioContainer><img :src="singleContent.imageUrl" :alt="singleContent.title"></RatioContainer></div>
            <span>{{singleContent.title}}</span>
          </li>

          <li v-if="contentSearch.length === 0 && isFetchingContent">
            <div class="f7 noresults">{{ $t('Loading Content...') }}</div>
          </li>

          <li v-if="contentSearch.length === 0 && !isFetchingContent">
            <div class="f7 noresults">{{ $t('No Results.') }}</div>
          </li>
        </ul>
      </div>

    </div>
    <div class="launcher f5">
      <input type="radio" id="launcher" :checked="launchChoice === 'launcher'" @change="setLaunchChoiceLauncher">
      <label for="launcher">
        <div class="iconcontainer"><UIIcon name="home" /></div>
        <span>{{ $t('Launcher') }}</span>
      </label>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UIInput from '@/core/shared/components/ui/UIInput';
import RatioContainer from '@/core/shared/components/utils/RatioContainer';
import handleClickOutside from '@/core/shared/helpers/handleClickOutside';

@Component({
  props: {
    contentSelection: Object,
    launchChoice: {
      type: String,
      required: true,
    },
    content: {
      type: Array,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    UIIcon,
    UIInput,
    RatioContainer,
  },
})
export default class ContentSelection extends Vue {
  open = false;
  search = '';
  clickOutsideListener = null;

  get contentSearch () {
    return this.content.filter(content => content.title.toLowerCase().includes(this.search.toLowerCase()));
  }

  get isFetchingContent () {
    return this.$store.state.content.isFetchingContent;
  }

  openContentList () {
    this.clickOutsideListener = handleClickOutside('.contentlist', this.open, () => { this.open = false; });
    this.open = true;
    this.search = '';
    document.querySelector('.contentlist').scrollTo(0, 0);
    document.querySelector('.contentlist input').focus();
    this.setLaunchChoiceContent();
  }

  async selectContent (content) {
    this.open = false;
    this.clickOutsideListener();
    this.$emit('update:contentSelection', content);
  }

  setLaunchChoiceContent () {
    this.$emit('update:launchChoice', 'content');
  }

  setLaunchChoiceLauncher () {
    this.$emit('update:launchChoice', 'launcher');
  }
}
</script>

<style lang="postcss" scoped>
@import 'core/shared/styles';

.content-selection {
  display: grid;
  grid-gap: var(--spacingSm);

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--spacingMd);

    & .title {
      grid-column: span 2;
    }
  }

  & input[type="radio"] {
    margin-right: var(--spacingSm);
  }

  & .content {
    position: relative;

    & .contentitem {
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 0;
      margin: 0;

      & .image {
        width: 7rem;
        margin-right: var(--spacingSm);
      }
    }

    & .selectionradio {
      display: flex;
      align-items: center;

      & .selectedcontent {
        flex: 1 1 auto;
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
      }
    }

    & .contentlist {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      padding: var(--spacingSm);
      box-shadow: var(--shadowMd);
      z-index: 1;
      border-radius: var(--radius);
      background-color: var(--colorWhite);
      height: 30rem;
      overflow: auto;
      transform-origin: top center;
      transform: scaleY(0) translateZ(0);
      transition: transform 0.15s ease 0.15s, opacity 0.15s ease 0.15s;
      opacity: 0;
      will-change: transform, opacity;
      pointer-events: none;

      @media (--tablet) {
        height: 50rem;
      }

      & .contentitem {
        padding: var(--spacingXS) 0;
      }

      & .contentitem,
      & > div {
        transform: translateZ(0);
        opacity: 0;
        will-change: opacity;
        transition: opacity 0.15s ease;
      }

      &._open {
        transform: scale(1) translateZ(0);
        transition: transform 0.15s ease, opacity 0.15s ease;
        opacity: 1;
        pointer-events: all;

        & .contentitem,
        & > div {
          opacity: 1;
          transform: translateZ(0);
          transition: opacity 0.3s ease 0.15s;
        }
      }

      & ul {
        display: grid;
        list-style-type: none;
        padding-left: 0;
        margin: var(--spacingXS) 0;
        background-color: var(--colorWhite);

        & .contentitem:hover {
          background-color: var(--colorCasper);
        }

        & .noresults {
          margin-top: var(--spacingSm);
          text-align: center;
        }
      }

      &.dark-mode {
        background-color: var(--colorSlate);

        ul {
          background-color: var(--colorSlate);

          & .contentitem:hover {
            background-color: var(--colorStorm);
          }
        }

        .ui-input {
          background-color: var(--darkModeColorDarkBackground) !important;
          color: var(--darkModeColorText) !important;
        }
      }

    }
  }

  & .launcher {
    display: flex;
    align-items: center;

    & label {
      display: flex;
      align-items: center;

      & .iconcontainer {
        flex: 0 0 auto;
        align-items: center;
        padding: var(--spacingXS) var(--spacingSm);
        margin-right: var(--spacingSm);
        border: 0.1rem solid var(--colorGeyser);

        @media (--tablet) {
          padding: var(--spacingXS) var(--spacingMd);
        }

        & svg {
          position: relative;
          top: 0.2rem;
          fill: var(--colorManatee);
        }
      }
    }

  }

}

</style>
